import {Typography} from '@mui/material';
import {AuthorizationFetcher, ConfigObjectType, PopupWrapper, FetcherUtils} from '@wallaby-ui/core';
import {MerieSchema, useFormatters} from '@wallaby-ui/document';
import React, {useCallback, useMemo, useState} from 'react';

export type Riskiness = MerieSchema.components['schemas']['PharmaRecommendationDto']['riskiness'];
export type Procedures = MerieSchema.components['schemas']['PharmaRecommendationDto']['procedures'];

type UseProceduresCountValidatorParams = {
  config: ConfigObjectType;
  evaluatedEncounterId: string;
  pharmaRecommendationId: number | undefined;
  riskiness: Riskiness;
  procedures: Procedures;
  riskFactorCount: number;
  instanceLocation: string | undefined;
  onCancelCallback?: () => void;
  onConfirmCallback?: () => void;
};

function appendParamsToSearchParams(
  searchParams: URLSearchParams,
  params: Omit<UseProceduresCountValidatorParams, 'config' | 'evaluatedEncounterId' | 'instanceLocation'>
): void {
  Object.entries(params).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((v) => searchParams.append(key, String(v)));
    } else if (value != null) {
      searchParams.append(key, String(value));
    }
  });
}

async function getNonReimbursableProcedures(
  config: ConfigObjectType,
  evaluatedEncounterId: string,
  pharmaRecommendationId: number | undefined,
  riskFactorCount: number,
  procedures: Procedures,
  riskiness: Riskiness
): Promise<MerieSchema.components['schemas']['PharmaRecommendationNonReimbursableProceduresDto']> {
  const requestUrl = new URL(
    config.service.url.hospitalizationPhRecommendationNonReimbursableProcedures.replace('{evaluatedEncounterId}', evaluatedEncounterId),
    window.location.origin
  );
  appendParamsToSearchParams(requestUrl.searchParams, {pharmaRecommendationId, riskFactorCount, procedures, riskiness});
  return AuthorizationFetcher.interceptedFetch(requestUrl, {method: 'get'}, {'Content-Type': 'application/json'}).then((response) =>
    FetcherUtils.processJsonResponse(response)
  );
}

export function useProceduresValidator({
  config,
  evaluatedEncounterId,
  pharmaRecommendationId,
  riskiness,
  procedures,
  riskFactorCount,
  instanceLocation,
  onCancelCallback,
  onConfirmCallback,
}: UseProceduresCountValidatorParams) {
  const [anchorTag, setAnchorTag] = useState<HTMLElement | null>(null);
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);
  const {t} = useFormatters();

  const validateProcedures = useCallback(
    async (action: () => void, targetElement?: HTMLElement) => {
      if (instanceLocation === 'CZ') {
        const result = await getNonReimbursableProcedures(
          config,
          evaluatedEncounterId,
          pharmaRecommendationId,
          riskFactorCount,
          procedures,
          riskiness
        );
        if (result.nonReimbursableProcedures.length === 0) {
          action();
        } else {
          setPendingAction(() => action);
          setAnchorTag(targetElement || null);
        }
      } else {
        action();
      }
    },
    [config, evaluatedEncounterId, pharmaRecommendationId, riskFactorCount, procedures, riskiness, instanceLocation]
  );

  const handleConfirm = useCallback(() => {
    pendingAction?.();
    setAnchorTag(null);
    setPendingAction(null);
    if (typeof onConfirmCallback === 'function') {
      onConfirmCallback();
    }
  }, [pendingAction]);

  const handleCancel = useCallback(() => {
    setAnchorTag(null);
    setPendingAction(null);
    if (typeof onCancelCallback === 'function') {
      onCancelCallback();
    }
  }, []);

  const modal = useMemo(() => {
    if (!anchorTag || !pendingAction) return null;

    return (
      <PopupWrapper
        openButton={false}
        anchorTag={anchorTag}
        direction='up'
        yOffset={4}
        showDiscardSaveButtons
        cancelTitle='common.confirmDialog.cancel'
        confirmTitle='common.confirmDialog.confirm'
        onClose={handleCancel}
        onConfirm={handleConfirm}
        data-name={'procedures.validation.dialog'}
      >
        <Typography variant='sectionHeader'>{t('hospitalization.detail.phRec.proc.validator.message')}</Typography>
      </PopupWrapper>
    );
  }, [anchorTag, pendingAction, handleCancel, handleConfirm, t]);

  return {validateProcedures, modal};
}
